// client/src/api/ApiMatches.js
import flex from './FlexFetch'

const matches_endpoint = '/api/1.0/matches/'

function getPayloadData(payload, { preserveSuccess = false } = {}) {
  if (
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    payload.success === true &&
    Object.prototype.hasOwnProperty.call(payload, 'data')
  ) {
    const { data } = payload
    if (
      preserveSuccess &&
      data &&
      typeof data === 'object' &&
      !Array.isArray(data)
    ) {
      return { success: true, ...data }
    }
    if (preserveSuccess) {
      return { success: true, data }
    }
    return data
  }
  return payload
}

// Submit a match score (sets and status)
async function submitScore(matchId, { sets, status }) {
  const response = await flex.flexFetch(
    matches_endpoint + `submitScores?matchId=${matchId}`,
    {
      method: 'POST',
      body: JSON.stringify({
        sets,
        status,
      }),
    },
  )
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload)
}

// Retrieve submitted scores for a match
async function getScores(matchId) {
  const response = await flex.flexFetch(
    matches_endpoint + `getScores?matchId=${matchId}`,
  )
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload)
}

// Schedule or reschedule a match
async function scheduleMatch(match_id, match_datetime) {
  const response = await flex.flexFetch(matches_endpoint + 'scheduleMatch', {
    method: 'POST',
    body: JSON.stringify({ match_id, match_datetime }),
  })
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload)
}

// Get basic match details including match_datetime and status
async function getMatch(matchId) {
  const response = await flex.flexFetch(
    matches_endpoint + `getMatchDetails?match_id=${matchId}`,
  )
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload)
}

// Get all matches for a league
async function getByLeague(leagueId) {
  const response = await flex.flexFetch(
    matches_endpoint + `getByLeague?league_id=${leagueId}`,
  )
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload)
}

// Get opponent statistics for a match
async function getOpponentStats(matchId, playerId) {
  const response = await flex.flexFetch(
    matches_endpoint +
      `getOpponentStats?match_id=${matchId}&player_id=${playerId}`,
  )
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload, { preserveSuccess: true })
}

// Get count of overdue matches that need scores entered
async function getOverdueMatchesCount() {
  const response = await flex.flexFetch(
    matches_endpoint + 'getOverdueMatchesCount',
  )
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload, { preserveSuccess: true })
}

// Cancel a match (delete it from the system)
async function cancelMatch(matchId) {
  const response = await flex.flexFetch(matches_endpoint + 'cancelMatch', {
    method: 'DELETE',
    body: JSON.stringify({ match_id: matchId }),
  })
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload, { preserveSuccess: true })
}

// Admin-only: Soft delete a match (set status to 'deleted')
async function deleteMatch(matchId) {
  const response = await flex.flexFetch(matches_endpoint + 'deleteMatch', {
    method: 'DELETE',
    body: JSON.stringify({ match_id: matchId }),
  })
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload, { preserveSuccess: true })
}

// Admin-only: Swap home and away players for a match
async function swapHomeAway(matchId) {
  const response = await flex.flexFetch(matches_endpoint + 'swapHomeAway', {
    method: 'POST',
    body: JSON.stringify({ match_id: matchId }),
  })
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload, { preserveSuccess: true })
}

// Admin-only: Get all upcoming scheduled matches across all leagues
async function getAllUpcomingMatches(options = {}) {
  const {
    limit = 50,
    offset = 0,
    leagueId,
    statusFilter,
    startDate,
    endDate,
    sortBy,
    sortDirection,
    includePast,
  } = options

  const params = new URLSearchParams()
  params.append('limit', limit.toString())
  params.append('offset', offset.toString())
  if (leagueId) params.append('leagueId', leagueId.toString())
  if (statusFilter) params.append('statusFilter', statusFilter)
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  if (sortBy) params.append('sortBy', sortBy)
  if (sortDirection) params.append('sortDirection', sortDirection)
  if (includePast !== undefined)
    params.append('includePast', includePast.toString())

  const response = await flex.flexFetch(
    matches_endpoint + `getAllUpcomingMatches?${params.toString()}`,
  )
  const payload = await flex.handleResponse(response)
  return getPayloadData(payload, { preserveSuccess: true })
}

const ApiMatches = {
  submitScore,
  getScores,
  scheduleMatch,
  getMatch,
  getByLeague,
  getOpponentStats,
  getOverdueMatchesCount,
  cancelMatch,
  deleteMatch,
  swapHomeAway,
  getAllUpcomingMatches,
}

export default ApiMatches
